import {
  LOCATION_OPTIONS,
  PAYMENT_FREQUENCIES,
} from 'constants/digitalLoan';
import { mapYearsAtAddressForApi } from 'constants/digitalLoanQuestionnaire';
import {
  MOCK_LOAN_RATE,
  PHASE2_MOCK_SCENARIOS,
} from 'constants/digitalLoanPhase2';
import {
  isQuestionnaireSubmissionValid,
  normalizeFullRfc,
} from 'routes/DigitalLoan/utils/questionnaireValidation';
import { requiresProofOfAddress } from 'routes/DigitalLoan/utils/locationHelpers';
import { mapLoanTerms } from 'routes/DigitalLoan/utils/loanTerms';
import { normalizePhase2MockScenario } from 'routes/DigitalLoan/utils/phase2MockMapping';
import DigitalLoanApi from './repository';
import {
  buildGuaranteeRequestPayload,
  isPhotoMatchBlocked,
  mapAmountAndScoringResponse,
  mapGuaranteeResponse,
  normalizeArticle,
  normalizeBrand,
  normalizeCatalogList,
  normalizeFamily,
  normalizeModel,
  normalizeSubFamily,
} from './catalogNormalize';
import {
  buildMockPreOffer,
  getMockArticlesData,
  getMockBrandsData,
  getMockFamiliesData,
  getMockModelsData,
  getMockSubFamiliesData,
} from './mocks';

const PROXIMITY_TYPE_MAP = Object.freeze({
  [LOCATION_OPTIONS.LIVE_NEARBY]: 'VIVO_CERCA',
  [LOCATION_OPTIONS.WORK_NEARBY]: 'TRABAJO_CERCA',
  [LOCATION_OPTIONS.BUSINESS_NEARBY]: 'NEGOCIO_CERCA',
  [LOCATION_OPTIONS.UNSURE]: 'NO_SEGURO',
});

const PAYMENT_FREQUENCY_MAP = Object.freeze({
  [PAYMENT_FREQUENCIES.MONTHLY]: 'MENSUAL',
  [PAYMENT_FREQUENCIES.BIWEEKLY]: 'CATORCENAL',
});

const PAYMENT_FREQUENCY_LABEL = Object.freeze({
  [PAYMENT_FREQUENCIES.MONTHLY]: 'Mensual',
  [PAYMENT_FREQUENCIES.BIWEEKLY]: 'Catorcenal',
});

function buildQuestionnaireRequestDTO(questionnaire) {
  const proximityType = PROXIMITY_TYPE_MAP[questionnaire.locationType];
  const { address } = questionnaire;
  const rfc = normalizeFullRfc(questionnaire.rfc);

  const payload = {
    proximityType,
    requestedAmount: questionnaire.requestedAmount,
    paymentFrequency: PAYMENT_FREQUENCY_MAP[questionnaire.paymentFrequency],
    requestedTerm: questionnaire.paymentTerm,
    address: {
      calle: address.street?.trim(),
      numExt: address.exteriorNumber?.trim(),
      cp: address.postalCode,
      entidad: address.stateName || address.state,
      alcaldia: address.municipalityName || address.municipality,
      colonia: address.colonyName || address.colony,
    },
    yearsAtAddress: mapYearsAtAddressForApi(address.yearsAtAddress),
    rfc,
    curp: questionnaire.curp,
    declaredMonthlyIncome: questionnaire.monthlyIncome,
  };

  if (address.interiorNumber?.trim()) {
    payload.address.numInt = address.interiorNumber.trim();
  }

  if (proximityType === 'NEGOCIO_CERCA') {
    payload.bussinessName = questionnaire.companyOrBusinessName.trim();
  }

  return payload;
}

function getProofOfAddressFile(questionnaire) {
  const proof = questionnaire.proofOfAddress;
  if (proof instanceof File || proof instanceof Blob) {
    return proof;
  }
  return null;
}

function getProofOfAddressFilename(proof) {
  if (proof?.name) {
    return proof.name;
  }
  return 'proof-of-address.jpg';
}

function delay(ms = 200) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function mapScenarioToResult(scenario) {
  switch (scenario) {
    case PHASE2_MOCK_SCENARIOS.VALIDATION_FAILED:
      return { status: PHASE2_MOCK_SCENARIOS.VALIDATION_FAILED };
    case PHASE2_MOCK_SCENARIOS.INSUFFICIENT_VALUE:
      return { status: PHASE2_MOCK_SCENARIOS.INSUFFICIENT_VALUE };
    case PHASE2_MOCK_SCENARIOS.MISSING_ONBOARDING:
      return { status: PHASE2_MOCK_SCENARIOS.MISSING_ONBOARDING };
    case PHASE2_MOCK_SCENARIOS.NOT_PREAPPROVED:
      return { status: PHASE2_MOCK_SCENARIOS.NOT_PREAPPROVED };
    case PHASE2_MOCK_SCENARIOS.SUCCESS:
    case PHASE2_MOCK_SCENARIOS.PREOFFER:
    default:
      return { status: PHASE2_MOCK_SCENARIOS.PREOFFER };
  }
}

function getPhotoFilename(file, index) {
  if (file?.name) return file.name;
  return `guarantee-photo-${index + 1}.jpg`;
}

export const startDigitalLoanApplication = (permissions) => async () => {
  const response = await DigitalLoanApi.startApplication({
    termsAccepted: !!permissions.terms,
    privacyAccepted: !!permissions.privacy,
    bureauConsent: !!permissions.bureau,
    // IMSS/ISSSTE permission is hidden by MLS request, but Backend contract still requires imssConsent=true.
    imssConsent: true,
  });

  return {
    applicationId: response?.applicationId || null,
    applicationNumber: response?.applicationNumber || null,
  };
};

export const checkBlackList = (applicationId) => async () => {
  const response = await DigitalLoanApi.checkBlackList(applicationId);
  return {
    isBlocked: !!response?.isBlocked,
    blockedReason: response?.blockedReason || '',
  };
};

export const validateCoordinates = (
  postalCode,
  latitude,
  longitude,
  applicationId,
) => async () => {
  const response = await DigitalLoanApi.validateCoordinates(
    postalCode,
    applicationId,
    {
      latitude: Number(latitude),
      longitude: Number(longitude),
    },
  );
  return {
    isValid: !!response?.isValid,
  };
};

export const submitApplicabilityQuestionnaire = (
  questionnaire,
  applicationId,
) => async () => {
  if (!applicationId) {
    throw new Error('Missing applicationId');
  }

  if (!isQuestionnaireSubmissionValid(questionnaire)) {
    throw new Error('Invalid questionnaire payload');
  }

  const proofRequired = requiresProofOfAddress(questionnaire.locationType);
  const proofOfAddress = getProofOfAddressFile(questionnaire);
  if (proofRequired && !proofOfAddress) {
    throw new Error('Missing proof of address file');
  }

  const questionnairePayload = buildQuestionnaireRequestDTO(questionnaire);
  const formData = new FormData();

  formData.append('questionnaire', JSON.stringify(questionnairePayload));

  if (proofRequired && proofOfAddress) {
    formData.append(
      'proofOfAddress',
      proofOfAddress,
      getProofOfAddressFilename(proofOfAddress),
    );
  }

  const response = await DigitalLoanApi.submitQuestionnaire(
    applicationId,
    formData,
  );

  return {
    applicationId: response?.applicationId || applicationId,
    currentPhase: response?.currentPhase || null,
    currentStep: response?.currentStep || null,
    completedAt: response?.completedAt || null,
    phase1Completed: response?.phase1Completed === true,
  };
};

export const getFamilies = () => async () => {
  const response = await DigitalLoanApi.getFamilies();
  return normalizeCatalogList(response, normalizeFamily);
};

export const getSubFamilies = (claveFamilia) => async () => {
  const response = await DigitalLoanApi.getSubFamilies({ claveFamilia });
  return normalizeCatalogList(response, normalizeSubFamily);
};

export const getArticles = (idFamiliaSubFamilia) => async () => {
  const response = await DigitalLoanApi.getArticles({ idFamiliaSubFamilia });
  return normalizeCatalogList(response, normalizeArticle);
};

export const getBranches = ({
  idFamiliaSubFamilia,
  idSubFamiliaTipoArticulo,
}) => async () => {
  const response = await DigitalLoanApi.getBranches({
    idFamiliaSubFamilia,
    idSubFamiliaTipoArticulo,
  });
  return normalizeCatalogList(response, normalizeBrand);
};

export const getModels = ({
  idMarca,
  idTipoArticulo,
  idSubfamiliaTipoArticulo,
  idFamiliaSubFamilia,
  familyCode,
}) => async () => {
  const response = await DigitalLoanApi.getModels({
    idMarca,
    idTipoArticulo,
    idSubfamiliaTipoArticulo,
    idFamiliaSubFamilia,
  });
  return normalizeCatalogList(
    response,
    (dto, index) => normalizeModel(dto, index, { familyCode }),
  );
};

/**
 * POST /digital-loan/{applicationId}/guarantee (multipart).
 * TEMP DEMO BYPASS: remover cuando carlos termine el back
 */
export const submitGuarantee = ({
  applicationId,
  phase2,
  allowMockSuccess = false,
}) => async () => {
  if (!applicationId) {
    throw new Error('Missing applicationId');
  }

  // TEMP DEMO BYPASS remover cuando carlos termine el back
  if (allowMockSuccess) {
    // eslint-disable-next-line no-console
    console.warn(
      'Digital Loan guarantee mock success used; real guarantee endpoint skipped.',
    );
    return {
      status: 'validated',
      valid: true,
      raw: null,
      demoBypass: true,
    };
  }

  const { guarantee, photos } = buildGuaranteeRequestPayload(phase2);
  const formData = new FormData();

  formData.append('guarantee', JSON.stringify(guarantee));

  photos.forEach((file, index) => {
    formData.append('photos', file, getPhotoFilename(file, index));
  });

  const response = await DigitalLoanApi.submitGuarantee(
    applicationId,
    formData,
  );
  const mapped = mapGuaranteeResponse(response);

  if (isPhotoMatchBlocked(mapped.photoMatchOutcome)) {
    return {
      status: PHASE2_MOCK_SCENARIOS.VALIDATION_FAILED,
      valid: false,
      ...mapped,
      raw: response,
    };
  }

  return {
    status: 'validated',
    valid: true,
    ...mapped,
    raw: response,
  };
};

/**
 * POST /digital-loan/{applicationId}/amount-and-scoring (sin body).
 */
export const runAmountAndScoring = ({
  applicationId,
  amount,
  term,
  rate,
  paymentFrequency,
  guaranteeName,
}) => async () => {
  if (!applicationId) {
    throw new Error('Missing applicationId');
  }

  const response = await DigitalLoanApi.runAmountAndScoring(applicationId);
  return mapAmountAndScoringResponse(response, {
    amount,
    term,
    rate,
    paymentFrequency,
    guaranteeName,
  });
};

export const getMockFamilies = () => async () => {
  await delay(150);
  return getMockFamiliesData();
};

export const getMockSubFamilies = (familyId) => async () => {
  await delay(150);
  return getMockSubFamiliesData(familyId);
};

export const getMockArticles = (subFamilyId) => async () => {
  await delay(150);
  return getMockArticlesData(subFamilyId);
};

export const getMockBrands = (articleId) => async () => {
  await delay(150);
  return getMockBrandsData(articleId);
};

export const getMockModels = (articleId) => async () => {
  await delay(150);
  return getMockModelsData(articleId);
};

/**
 * QA/dev only: fuerza branch mock vía digitalLoanPhase2Mock.
 */
export const submitMockGuarantee = (payload = {}) => async () => {
  await delay(250);
  const scenario = normalizePhase2MockScenario(payload.scenario);
  const mapped = mapScenarioToResult(scenario);

  if (mapped.status === PHASE2_MOCK_SCENARIOS.VALIDATION_FAILED) {
    return {
      status: PHASE2_MOCK_SCENARIOS.VALIDATION_FAILED,
      valid: false,
    };
  }

  if (mapped.status === PHASE2_MOCK_SCENARIOS.INSUFFICIENT_VALUE) {
    return {
      status: PHASE2_MOCK_SCENARIOS.INSUFFICIENT_VALUE,
      valid: false,
    };
  }

  return {
    status: 'validated',
    valid: true,
    scenario,
  };
};

/**
 * QA/dev only: scoring mock forzado.
 */
export const runMockAmountAndScoring = (payload = {}) => async () => {
  await delay(250);
  const scenario = normalizePhase2MockScenario(payload.scenario);
  const mapped = mapScenarioToResult(scenario);

  if (
    mapped.status === PHASE2_MOCK_SCENARIOS.VALIDATION_FAILED
    || mapped.status === PHASE2_MOCK_SCENARIOS.INSUFFICIENT_VALUE
  ) {
    return {
      status: mapped.status,
      valid: false,
    };
  }

  if (mapped.status === PHASE2_MOCK_SCENARIOS.MISSING_ONBOARDING) {
    return { status: PHASE2_MOCK_SCENARIOS.MISSING_ONBOARDING };
  }

  if (mapped.status === PHASE2_MOCK_SCENARIOS.NOT_PREAPPROVED) {
    return {
      status: PHASE2_MOCK_SCENARIOS.NOT_PREAPPROVED,
      summary: {
        amount: payload.amount,
        term: payload.term,
        paymentFrequency: PAYMENT_FREQUENCY_LABEL[payload.paymentFrequency]
          || 'Mensual',
        paymentDay: '14 de cada mes',
        rate: payload.rate ?? MOCK_LOAN_RATE,
      },
    };
  }

  return {
    status: PHASE2_MOCK_SCENARIOS.PREOFFER,
    preOffer: buildMockPreOffer({
      amount: payload.amount,
      term: payload.term,
      paymentFrequencyLabel: PAYMENT_FREQUENCY_LABEL[payload.paymentFrequency]
        || 'Mensual',
      guaranteeName: payload.guaranteeName || 'Prenda',
    }),
  };
};

/**
 * POST /digital-loan/{applicationId}/calculate-new-loan-amount
 */
export const calculateNewLoanAmount = ({
  applicationId,
  newTerm,
  newRequestedAmount,
  percentageRate,
}) => async () => {
  if (!applicationId) {
    throw new Error('Missing applicationId');
  }

  const response = await DigitalLoanApi.calculateNewLoanAmount(applicationId, {
    newTerm: Number(newTerm),
    newRequestedAmount: Number(newRequestedAmount),
    percentageRate: Number(percentageRate),
  });

  return {
    applicationId: response?.applicationId || applicationId,
    requestedAmount: response?.requestedAmount ?? Number(newRequestedAmount),
    requestedTerm: response?.requestedTerm ?? Number(newTerm),
    percentageRate: response?.percentageRate ?? Number(percentageRate),
    sueldoNecesario: response?.sueldoNecesario ?? null,
    maxLoanAmount: response?.maxLoanAmount ?? null,
    dpPercentageUsed: response?.dpPercentageUsed ?? null,
    viable: response?.viable === true,
    calculatedAt: response?.calculatedAt || null,
    raw: response,
  };
};

export const getDigitalLoanStatus = () => async () => {
  const response = await DigitalLoanApi.getStatus();
  return {
    hasActiveApplication: response?.hasActiveApplication === true,
    hasApplicationAwaitingDisbursement:
      response?.hasApplicationAwaitingDisbursement === true,
    activeApplication: response?.activeApplication || null,
    questionnaire: response?.questionnaire || null,
    guarantee: response?.guarantee || null,
    valuation: response?.valuation || null,
    gestoria: response?.gestoria || null,
    raw: response,
  };
};

export const acceptPreapproval = (applicationId, payload = {}) => async () => {
  if (!applicationId) {
    throw new Error('Missing applicationId');
  }

  const acceptedAmount = Number(payload.acceptedAmount);
  if (!Number.isFinite(acceptedAmount) || acceptedAmount <= 0) {
    throw new Error('Missing acceptedAmount');
  }

  const response = await DigitalLoanApi.acceptPreapproval(applicationId, {
    acceptedAmount,
  });

  return {
    applicationId: response?.applicationId || applicationId,
    preapprovalAmount: response?.preapprovalAmount ?? acceptedAmount,
    preapprovalAcceptedAt: response?.preapprovalAcceptedAt || null,
    currentPhase: response?.currentPhase || null,
    currentStep: response?.currentStep || null,
    maxReschedules: Number.isFinite(Number(response?.maxReschedules))
      ? Number(response.maxReschedules)
      : null,
    raw: response,
  };
};

function mapVisitScheduleResponse(response, applicationId) {
  const data = response || {};
  return {
    applicationId: data.applicationId || applicationId,
    scheduledVisitAt: data.scheduledVisitAt || null,
    rescheduleCount: Number.isFinite(Number(data.rescheduleCount))
      ? Number(data.rescheduleCount)
      : 0,
    maxReschedules: Number.isFinite(Number(data.maxReschedules))
      ? Number(data.maxReschedules)
      : null,
    gestoriaProvider: data.gestoriaProvider || null,
    gestoriaFolio: data.gestoriaFolio || null,
    dispatchedAt: data.dispatchedAt || null,
    selfieSent: data.selfieSent === true,
    ineSent: data.ineSent === true,
    currentPhase: data.currentPhase || null,
    currentStep: data.currentStep || null,
    raw: data,
  };
}

export const scheduleVisit = (applicationId, payload = {}) => async () => {
  if (!applicationId) {
    throw new Error('Missing applicationId');
  }
  if (!payload.scheduledVisitAt) {
    throw new Error('Missing scheduledVisitAt');
  }

  const body = {
    scheduledVisitAt: payload.scheduledVisitAt,
  };
  if (payload.indicaciones) {
    body.indicaciones = payload.indicaciones;
  }

  const response = await DigitalLoanApi.scheduleVisit(applicationId, body);
  return mapVisitScheduleResponse(response, applicationId);
};

export const rescheduleVisit = (applicationId, payload = {}) => async () => {
  if (!applicationId) {
    throw new Error('Missing applicationId');
  }
  if (!payload.newVisitAt) {
    throw new Error('Missing newVisitAt');
  }

  const body = {
    newVisitAt: payload.newVisitAt,
  };
  if (payload.reason) {
    body.reason = payload.reason;
  }

  const response = await DigitalLoanApi.rescheduleVisit(applicationId, body);
  return mapVisitScheduleResponse(response, applicationId);
};

export const cancelVisit = (applicationId, payload = {}) => async () => {
  if (!applicationId) {
    throw new Error('Missing applicationId');
  }

  const body = {};
  if (payload.reason) {
    body.reason = payload.reason;
  }

  const response = await DigitalLoanApi.cancelVisit(applicationId, body);
  return mapVisitScheduleResponse(response, applicationId);
};

function mapOffer(response) {
  const data = response || {};
  return {
    applicationId: data.applicationId || null,
    offerAmount: data.offerAmount ?? null,
    offerTerm: data.offerTerm ?? null,
    offerVersion: data.offerVersion || null,
    commission: data.commission ?? null,
    interestRate: data.interestRate ?? null,
    amountWithCommission: data.amountWithCommission ?? null,
    paymentType: data.paymentType || null,
    paymentFrequency: data.paymentFrequency || null,
    product: data.product ?? null,
    totalToPay: data.totalToPay ?? null,
    paymentSchedule: Array.isArray(data.paymentSchedule)
      ? data.paymentSchedule
      : [],
    offerExpiresAt: data.offerExpiresAt || null,
    offerAcceptedAt: data.offerAcceptedAt || null,
    operationalWindowOk: typeof data.operationalWindowOk === 'boolean'
      ? data.operationalWindowOk
      : null,
    pendingUntil: data.pendingUntil || null,
    currentPhase: data.currentPhase || null,
    currentStep: data.currentStep || null,
    raw: data,
  };
}

/**
 * GET /digital-loan/loan-terms.
 * Plazos y tasas salen del tarifario; no se sustituyen en el cliente.
 */
export const getLoanTerms = () => async () => {
  const response = await DigitalLoanApi.getLoanTerms();
  return mapLoanTerms(response);
};

/**
 * GET /digital-loan/{applicationId}/offer?plazo=&periodicidad=
 * La última consulta exitosa es la oferta que persiste el backend.
 * periodicidad va como MENSUAL o CATORCENAL, no como código de producto.
 */
export const getOffer = (applicationId, query = {}) => async () => {
  if (!applicationId) throw new Error('Missing applicationId');
  const response = await DigitalLoanApi.getOffer(applicationId, {
    plazo: query.plazo,
    periodicidad: query.periodicidad,
  });
  return mapOffer(response);
};

/** POST /offer/reject sin body. Cierra la solicitud. */
export const rejectOffer = (applicationId) => async () => {
  if (!applicationId) throw new Error('Missing applicationId');
  const response = await DigitalLoanApi.rejectOffer(applicationId);
  return {
    applicationId: response?.applicationId || applicationId,
    status: response?.status || null,
    rejectionReason: response?.rejectionReason || null,
    finishedAt: response?.finishedAt || null,
  };
};

/**
 * POST /offer/accept con el monto y plazo de esa misma oferta.
 * No usa montos de Fase 2 ni de la preaprobación.
 */
export const acceptOffer = (applicationId, offer) => async () => {
  if (!applicationId) throw new Error('Missing applicationId');
  const acceptedAmount = Number(offer?.offerAmount);
  const acceptedTerm = Number(offer?.offerTerm);
  if (!Number.isFinite(acceptedAmount) || !Number.isFinite(acceptedTerm)) {
    throw new Error('Missing offer amount');
  }
  const response = await DigitalLoanApi.acceptOffer(applicationId, {
    acceptedAmount,
    acceptedTerm,
  });
  return {
    applicationId: response?.applicationId || applicationId,
    currentPhase: response?.currentPhase || null,
    currentStep: response?.currentStep || null,
    raw: response,
  };
};

function mapClabeResponse(response, applicationId) {
  return {
    applicationId: response?.applicationId || applicationId,
    clabeMasked: response?.clabeMasked || null,
    status: response?.status || null,
    providerCode: response?.providerCode ?? null,
    providerMessage: response?.providerMessage || null,
    validatedAt: response?.validatedAt || null,
    currentStep: response?.currentStep || null,
    raw: response,
  };
}

/** POST /clabe. Solo el número. No se registra en logs ni en storage. */
export const captureClabe = (applicationId, clabe) => async () => {
  if (!applicationId) throw new Error('Missing applicationId');
  const digits = String(clabe || '').replace(/\D/g, '');
  if (!/^\d{18}$/.test(digits)) {
    throw new Error('Invalid clabe');
  }
  const response = await DigitalLoanApi.captureClabe(applicationId, {
    clabe: digits,
  });
  return mapClabeResponse(response, applicationId);
};

/** GET /clabe. Una lectura; no inicia un ciclo de consulta. */
export const getClabe = (applicationId) => async () => {
  if (!applicationId) throw new Error('Missing applicationId');
  const response = await DigitalLoanApi.getClabe(applicationId);
  return mapClabeResponse(response, applicationId);
};

/**
 * POST /biometric-final. passed y fingerprint son obligatorios.
 * El llamador no debe persistir fingerprint.
 */
export const registerBiometricFinal = (
  applicationId,
  capture,
) => async () => {
  if (!applicationId) throw new Error('Missing applicationId');
  if (
    typeof capture?.passed !== 'boolean'
    || typeof capture?.fingerprint !== 'string'
    || !capture.fingerprint
  ) {
    throw new Error('Missing biometric fingerprint');
  }
  const response = await DigitalLoanApi.registerBiometricFinal(
    applicationId,
    {
      passed: capture.passed,
      fingerprint: capture.fingerprint,
    },
  );
  return {
    applicationId: response?.applicationId || applicationId,
    passed: response?.passed === true,
    biometricFinalAt: response?.biometricFinalAt || null,
    currentStep: response?.currentStep || null,
    raw: response,
  };
};

/** POST /contract sin body. */
export const generateContract = (applicationId) => async () => {
  if (!applicationId) throw new Error('Missing applicationId');
  const response = await DigitalLoanApi.generateContract(applicationId);
  return {
    applicationId: response?.applicationId || applicationId,
    generated: response?.generated === true,
    contractNumber: response?.contractNumber || null,
    loanAmount: response?.loanAmount ?? null,
    term: response?.term ?? null,
    contractGeneratedAt: response?.contractGeneratedAt || null,
    operationalWindowOk: typeof response?.operationalWindowOk === 'boolean'
      ? response.operationalWindowOk
      : null,
    pendingUntil: response?.pendingUntil || null,
    currentPhase: response?.currentPhase || null,
    currentStep: response?.currentStep || null,
    status: response?.status || null,
    raw: response,
  };
};

/**
 * GET /simulation/documents. Un tipo por apertura.
 * tipo vacío pide Cláusulas. El 01 no se solicita antes del contrato.
 */
export const getSimulationDocument = (
  applicationId,
  tipo = '',
) => async () => {
  if (!applicationId) throw new Error('Missing applicationId');
  if (String(tipo) === '01') {
    throw new Error('El mutuo solo existe después del contrato');
  }
  const response = await DigitalLoanApi.getSimulationDocument(
    applicationId,
    tipo,
  );
  return {
    applicationId: response?.applicationId || applicationId,
    documentType: response?.documentType ?? tipo,
    contractNumber: response?.contractNumber || null,
    file: response?.file || '',
    fileLength: response?.fileLength ?? null,
  };
};

/** GET documento de contrato. Un tipo por llamada. */
export const getContractDocument = (applicationId, tipo) => async () => {
  if (!applicationId) throw new Error('Missing applicationId');
  if (!tipo) throw new Error('Missing document type');
  const response = await DigitalLoanApi.getContractDocument(
    applicationId,
    tipo,
  );
  return {
    applicationId: response?.applicationId || applicationId,
    documentType: response?.documentType || tipo,
    contractNumber: response?.contractNumber || null,
    file: response?.file || '',
    fileLength: response?.fileLength ?? null,
  };
};

/**
 * GET /digital-loan/business-calendar.
 * Un día con descripción vacía sigue siendo un día inhábil.
 */
export const getBusinessCalendar = () => async () => {
  const response = await DigitalLoanApi.getBusinessCalendar();
  const days = Array.isArray(response?.days) ? response.days : [];
  return {
    from: response?.from || null,
    to: response?.to || null,
    startHour: response?.startHour || null,
    endHour: response?.endHour || null,
    days: days.map((day) => ({
      date: day?.date || '',
      description: day?.description ?? '',
      source: day?.source || '',
    })),
    raw: response,
  };
};

/**
 * QA/dev only: recálculo forzado cuando digitalLoanPhase2Mock está activo.
 * No usar en flujo normal (usar calculateNewLoanAmount).
 */
export const recalculateForcedMockLoan = ({ amount, term, rate }) => async () => {
  await delay(200);
  const payload = {
    amount,
    term,
    rate: rate ?? MOCK_LOAN_RATE,
  };

  return {
    ...payload,
    accepted: true,
    viable: true,
    maxLoanAmount: amount,
    forcedMock: true,
  };
};

export { buildQuestionnaireRequestDTO, PAYMENT_FREQUENCY_LABEL };
